import type { ApolloQueryResult, ApolloClient } from 'apollo-client';
import type { DocumentNode } from 'graphql';

import { SSRMeasures } from '@confluence/action-measures';
import { getMonitoringClient } from '@confluence/monitoring';

import { SessionDataQuery } from './SessionDataQuery.graphql';
import type { SessionDataQuery as SessionDataQueryType } from './__types__/SessionDataQuery';
import type { SessionDataType } from './SessionDataTypes';
import { SiteInformationQuery } from './SiteInformationQuery.graphql';
import type { SiteInformationQuery as SiteInformationQueryType } from './__types__/SiteInformationQuery';
import type { FeatureFlagsQuery as FeatureFlagsQueryType } from './__types__/FeatureFlagsQuery';

declare global {
	interface Window {
		__SITEINFORMATION_PROMISE__: Promise<SessionDataType> | null;
		__SITEINFORMATION__:
			| Promise<(SiteInformationQueryType & FeatureFlagsQueryType) | null>
			| (SiteInformationQueryType & FeatureFlagsQueryType)
			| Promise<null>
			| null;
		__LD_FEATURE_FLAGS__?: FeatureFlagsQueryType;
		__ENABLE_SITE_INFORMATION_QUERY__?: boolean;
		__ENABLE_TOLERANT_SESSION_DATA_ERROR_HANDLING__?: boolean;
	}
}

function apolloQuery<Q>({
	query,
	queryName,
	client = window.GLOBAL_APOLLO_CLIENT,
}: {
	query: DocumentNode;
	queryName: string;
	client?: ApolloClient<any>;
}): Promise<{ data: Q | null }> {
	if (!client) {
		return Promise.reject(new Error(`ApolloClient not available, cannot load query ${queryName}`));
	}

	return SSRMeasures.run(`ssr-app/prepare/preloadQuery/fetch:${queryName}`, async () =>
		client.query({
			query,
			errorPolicy: 'all',
			fetchPolicy: 'cache-first',
			context: {
				single: false,
				allowOnExternalPage: true,
			},
		}),
	);
}

// Since all LD FFs are archived, we can safely assume that both featureFlags and platformFeatureFlags are empty.
const emptyFeatureFlagsRaw: FeatureFlagsQueryType = {
	featureFlags: {
		nodes: [],
	},
	platformFeatureFlags: {
		nodes: [],
	},
};

export async function loadSessionData(): Promise<
	(SiteInformationQueryType & FeatureFlagsQueryType) | null
> {
	// __SITEINFORMATION__ is either actual session data
	// or the promise that is resolving the session data
	// In the case when page is rendered by SSR, the __SITEINFORMATION__ will be populated with JSON data.
	if (window['__SITEINFORMATION__']) {
		return window['__SITEINFORMATION__'];
	}

	const queries: any = [];
	const isSiteInformationQueryEnabledValue =
		window['__ENABLE_SITE_INFORMATION_QUERY__'] || process.env.NODE_ENV === 'testing';
	queries.push(
		apolloQuery<SiteInformationQueryType | SessionDataQueryType | null>({
			query: isSiteInformationQueryEnabledValue ? SiteInformationQuery : SessionDataQuery,
			queryName: 'SiteInformationQuery',
		}),
	);

	// Maintain compatibility with old code that expects __LD_FEATURE_FLAGS__ to be set.
	window['__LD_FEATURE_FLAGS__'] = emptyFeatureFlagsRaw;

	window['__SITEINFORMATION__'] = Promise.all(queries)
		.then((queriesRes) => {
			const SiteInformationQueryResult =
				queriesRes[0] as ApolloQueryResult<SiteInformationQueryType | null>;

			const featureFlagsQueryResult = { data: emptyFeatureFlagsRaw };

			const errors = SiteInformationQueryResult.errors ?? [];

			if (errors.length > 0) {
				errors.forEach((e) => getMonitoringClient().submitError(e, { attribution: 'FATAL' }));
				// #hot-116748:
				// We only respect FG value __ENABLE_TOLERANT_SESSION_DATA_ERROR_HANDLING__ when SSR.
				// Because FG is not available in non-SSR at this stage.
				// For non-SSR, we act as if FG is true, and use the errorAllowedFieldSetNONSSR
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				if (!window['__ENABLE_TOLERANT_SESSION_DATA_ERROR_HANDLING__'] && process.env.REACT_SSR) {
					throw errors;
				}
			}

			let result: (SiteInformationQueryType & FeatureFlagsQueryType) | null = null;

			if (SiteInformationQueryResult.data && featureFlagsQueryResult.data) {
				result = {
					...SiteInformationQueryResult.data,
					...featureFlagsQueryResult.data,
				};
			}
			return result;
		})
		.catch((error) => {
			if (Array.isArray(error)) {
				error.forEach((e) => getMonitoringClient().submitError(e, { attribution: 'FATAL' }));
			} else {
				getMonitoringClient().submitError(error, { attribution: 'FATAL' });
			}

			return null;
		});

	return window['__SITEINFORMATION__'];
}
